import { faSpinner, faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import isEqual from "fast-deep-equal/es6";
import { useEffect, useMemo, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import { useAccount, useHeightData, useSelection, useWorkspaces } from "../state";
import { useOwnership } from "../state/ownership";
import { Cube, Workspace } from "../state/types";
import { CTABox } from "./CTABox";
import type { OwnershipProps } from "./sections/Ownership";
import { SectionBase } from "./sections/SectionBase";
import { SectionBuild } from "./sections/SectionBuild";
import { SectionFloraFauna } from "./sections/SectionFloraFauna";
import { SectionHero } from "./sections/SectionHero";
import { SectionSoil } from "./sections/SectionSoil";
import { SectionSurroundings } from "./sections/SectionSurroundings";
import { SectionHeader } from "./sections/util";

// Het rapportpaneel begint op mobiel onder de kaart (80vh) en op desktop
// onder de header; op mobiel houdt het de zijbalk vrij.
export const REPORT_PANEL = "min-h-screen top-[80vh] sm:top-14 max-sm:w-[calc(100vw-40px)]";

interface HPprops {
  cube: Cube;
  title: string;
  icon: React.ReactNode;
  children?: React.ReactNode;
}
const H = ({ cube, children, ...props }: HPprops) => (
  <div className="shrink bg-white">
    <SectionHero field={cube} />
    <div className="p-4 flex flex-col">
      <SectionHeader {...props} />
    </div>
    {children}
    <div className="bg-white h-full" />
  </div>
);

export const Intermediate = ({ state, cube, error }) => {
  const e = state === "error" && error;
  if (state === "fetching")
    return (
      <H
        cube={cube}
        title="Laden..."
        icon={
          <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin-slow" fill="green" />
        }
      />
    );
  return (
    <H
      cube={cube}
      icon={<FontAwesomeIcon icon={faXmark} className="w-4 h-4" fill="red" />}
      title="Fout..."
    >
      <div className="p-4">
        {e === 404 ? "Geen data gevonden op deze plek. " : "Fout bij het laden van de data. "}
        <Link className="text-blue-500 hover:text-blue-700" to="/">
          Ga terug naar de kaart.
        </Link>
      </div>
    </H>
  );
};

const ReportData = ({ location }) => {
  const gps = useMemo(() => {
    if (!location) return;
    const loc = location.split(",").map(parseFloat);
    if (loc.length === 2 && !isNaN(loc[0]) && !isNaN(loc[1])) return loc;
  }, [location]);
  const [selectedGPS, cube, state, error, selectGPS] = useSelection(
    useShallow((s) => [s.gps, s.cube, s.state, s.error, s.selectGPS])
  );
  const clear = useHeightData((s) => s.clear);
  useEffect(() => {
    if (gps && !isEqual(gps, selectedGPS)) {
      clear();
      selectGPS(gps);
    }
  }, [gps, clear, selectGPS, selectedGPS]);
  const loggedIn = useAccount((s) => Boolean(s.user));

  // Eigendom alleen bij een perceel uit het geselecteerde eigen project,
  // gekoppeld op naam zoals in CTABox.
  const workspace: Workspace | undefined = useWorkspaces((s) =>
    s.workspaces?.find((p) => p.id === s.selectedWorkspaceId)
  );
  const parcel = workspace?.parcels?.find((p) => p.name === cube?.name);
  const ownershipRows = useOwnership(
    loggedIn && workspace && parcel ? `/api/workspaces/${workspace.id}/ownership` : undefined
  );

  // Only trust the store's state once it actually reflects this component's gps.
  // Until then, the values in the store belong to a previous location and would
  // flash stale "fetched" or "error" UI.
  const inSync = isEqual(gps, selectedGPS);
  if (!inSync || state !== "fetched") {
    return (
      <Intermediate
        state={inSync ? state : "fetching"}
        cube={inSync ? cube : undefined}
        error={inSync ? error : undefined}
      />
    );
  }
  return (
    <CubeSections
      cube={cube}
      ownership={
        parcel && {
          rows: ownershipRows?.filter((row) => row.parcelId === parcel.id),
          hintWhenMissing: true,
        }
      }
    >
      <CTABox field={cube} show={loggedIn} />
    </CubeSections>
  );
};

export const CubeSections = ({
  cube,
  coords,
  embedded = false,
  ownership,
  children,
}: {
  cube: Cube;
  coords?: string;
  // Zie SectionHero: binnen de workspace hoort de paginachrome er niet bij.
  embedded?: boolean;
  // Eigendom (Kadaster); alleen waar de kijker het mag zien, dus de aanroeper
  // kiest het endpoint. Zonder deze prop geen blok.
  ownership?: OwnershipProps;
  children?: React.ReactNode;
}) => (
  <div className="shrink">
    <SectionHero field={cube} coords={coords} embedded={embedded} />
    <div className="p-4 flex flex-col">
      <SectionBase field={cube} />
      <SectionBuild field={cube} ownership={ownership} />
      <SectionSurroundings field={cube} />
      <SectionSoil field={cube} />
      <SectionFloraFauna field={cube} />
      {/* Zorgt dat de laatste sectie tot bovenin kan scrollen (actief in het menu). */}
      <div className="bg-white h-[calc(100vh-260px)]" />
    </div>
    {children}
  </div>
);

type RReportType = {
  location: string;
  updateSection: (section: string) => void;
  className?: string;
  Component?: React.ComponentType<{ location: string }>;
};
export const RReport = ({
  location,
  updateSection,
  className,
  Component = ReportData,
}: RReportType) => {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    const handler = () => {
      if (!ref?.current) return;
      let i;
      const sections = ref.current?.getElementsByTagName("section");
      for (i = 0; i < sections.length && sections[i].offsetTop - (83 + 56) < window.scrollY; i++);
      const activeSection = (i > 1 && sections[i - 1].id?.substring(8)) || "";
      updateSection(activeSection);
    };
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, [ref, updateSection]);
  useEffect(() => {
    if (window.innerWidth >= 640) return;
    window.scrollTo({ top: window.innerHeight * 0.4, behavior: "auto" });
  }, []);
  return (
    <aside
      className={
        className ||
        `relative sm:w-[24rem] lg:w-[27rem] left-10 sm:left-20 flex z-10 ${REPORT_PANEL}`
      }
      ref={ref}
    >
      <div className="rounded-t-2xl bg-white w-full flex flex-col items-stretch justify-start">
        <Component location={location} />
      </div>
    </aside>
  );
};

const mp = { base: "" };
export const Report = () => {
  const [p0, p1] = useParams()["*"].split("/");
  const navigate = useNavigate();
  const updateSection = useMemo(
    () => (section: string) => {
      if (p1 !== section) navigate(`/location/${mp[p0] || p0}/${section}`, { replace: true });
    },
    [navigate, p0, p1]
  );

  return <RReport location={p0} updateSection={updateSection} />;
};
